import { throttle } from 'lodash';

/**
 * Fetches the text content of a given URL.
 *
 * @param {string} url - The URL to fetch content from.
 * @returns {Promise<string|false>} The text content from the fetched URL, or false if the URL is not provided.
 */
const fetchPageText = (url) => {
  if (!url) return false;
  return fetch(url).then((res) => res.text());
};

/**
 * Selects elements based on provided selectors from the given HTML string or DOM.
 *
 * @param {Object} params - The parameters object.
 * @param {string} [params.data] - HTML string to parse.
 * @param {Document} [params.dom] - DOM document to use for querying.
 * @param {Object} params.selectors - Selectors for the elements to select.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} [params.selectors.counter='[data-pagination-counter]'] - The selector for the counter element.
 * @param {string} [params.selectors.loadButton='.pagination__load-more'] - The selector for the load button.
 * @param {string} [params.selectors.contentWrapper='.pagination__content-wrapper'] - The selector for the content wrapper.
 * @returns {Object} The selected elements: `counterEl`, `loadButtonEl`, and `contentWrapperEl`.
 */
const selectElements = ({
  data,
  dom,
  selectors: {
    parent,
    counter = '[data-pagination-counter]',
    loadButton = '.pagination__load-more',
    contentWrapper = '.pagination__content-wrapper',
  },
}) => {
  if (!data && !dom) return {};
  const domEl = dom ? dom : new DOMParser().parseFromString(data, 'text/html');
  const parentEl = parent ? domEl.querySelector(parent) : domEl;
  return {
    counterEl: parentEl.querySelector(counter),
    loadButtonEl: parentEl.querySelector(loadButton),
    contentWrapperEl: parentEl.querySelector(contentWrapper),
  };
};

/**
 * Updates the current browser URL using the History API.
 *
 * @param {string} targetUrl - The target URL to update to.
 */
const updateUrl = (targetUrl) => {
  history.replaceState(
    null,
    '',
    targetUrl.indexOf(window.location.origin) >= 0 ? targetUrl : `${window.location.origin}${targetUrl}`,
  );
};

/**
 * Updates the content of the page by inserting new content before or after existing content.
 *
 * @param {Object} params - The parameters object.
 * @param {boolean} [params.insertAfter=true] - Whether to insert the new content after the existing content.
 * @param {Promise<string>} params.data - The HTML string to insert.
 * @param {Object} params.components - The current DOM components.
 * @param {HTMLElement} params.components.counterEl - The counter element.
 * @param {HTMLElement} params.components.loadButtonEl - The load button element.
 * @param {HTMLElement} params.components.contentWrapperEl - The content wrapper element.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} params.selectors.counter - The selector for the counter element.
 * @param {string} params.selectors.loadButton - The selector for the load button.
 * @param {string} params.selectors.contentWrapper - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const updateContent = async ({
  insertAfter = true,
  data,
  components: { counterEl = false, loadButtonEl, contentWrapperEl },
  selectors: { parent, counter, loadButton, contentWrapper },
  callbacks: { beforeFetch, afterFetch },
} = {}) => {
  if (typeof beforeFetch == 'function')
    beforeFetch({
      insertAfter,
      components: { counterEl, loadButtonEl, contentWrapperEl },
    });
  const {
    counterEl: newCounterEl,
    loadButtonEl: newLoadButtonEl,
    contentWrapperEl: newContentWrapperEl,
  } = selectElements({
    data: await data,
    selectors: { parent, counter, loadButton, contentWrapper },
  });

  if (!newLoadButtonEl || !newContentWrapperEl) return;
  if (insertAfter) {
    updateUrl(loadButtonEl.dataset.nextUrl);
    if (newCounterEl && counterEl) counterEl.innerText = newCounterEl.innerText;
    loadButtonEl.dataset.nextUrl = newLoadButtonEl.dataset.nextUrl;
    contentWrapperEl.append(...Array.from(newContentWrapperEl.children));
  } else {
    loadButtonEl.dataset.previousUrl = newLoadButtonEl.dataset.previousUrl;
    contentWrapperEl.prepend(...Array.from(newContentWrapperEl.children));
  }

  if (typeof afterFetch == 'function')
    afterFetch({
      insertAfter,
      components: { counterEl, loadButtonEl, contentWrapperEl },
    });
};

/**
 * Reloads the previous pages of content by fetching and prepending them to the current content.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.components - The current DOM components.
 * @param {HTMLElement} params.components.loadButtonEl - The load button element.
 * @param {HTMLElement} params.components.contentWrapperEl - The content wrapper element.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} params.selectors.loadButton - The selector for the load button.
 * @param {string} params.selectors.contentWrapper - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const reloadPreviousPages = async ({
  components: { loadButtonEl, contentWrapperEl },
  selectors: { parent, loadButton, contentWrapper },
  callbacks: { beforeFetch, afterFetch },
}) => {
  if (!loadButtonEl || !contentWrapperEl || !loadButtonEl?.dataset?.previousUrl) return;
  await updateContent({
    insertAfter: false,
    data: fetchPageText(loadButtonEl.dataset.previousUrl),
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });
  reloadPreviousPages({
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });
};

/**
 * Initializes the pagination functionality on the page.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.selectors - The selectors for various elements.
 * @param {string} params.selectors.parent - The selector for the parent element.
 * @param {string} [params.selectors.counter='[data-pagination-counter]'] - The selector for the counter element.
 * @param {string} [params.selectors.loadButton='.pagination__load-more'] - The selector for the load button.
 * @param {string} [params.selectors.contentWrapper='.pagination__content-wrapper'] - The selector for the content wrapper.
 * @param {Object} params.callbacks - The callback functions.
 * @param {Function} [params.callbacks.beforeFetch] - The function to call before fetching the new content.
 * @param {Function} [params.callbacks.afterFetch] - The function to call after fetching and updating the content.
 */
const initPagination = ({
  selectors: {
    parent,
    counter = '[data-pagination-counter]',
    loadButton = '.pagination__load-more',
    contentWrapper = '.pagination__content-wrapper',
  } = {
    counter: '[data-pagination-counter]',
    loadButton: '.pagination__load-more',
    contentWrapper: '.pagination__content-wrapper',
  },
  callbacks: { beforeFetch, afterFetch } = {},
} = {}) => {
  const { counterEl, loadButtonEl, contentWrapperEl } = selectElements({
    dom: document,
    selectors: { parent, counter, loadButton, contentWrapper },
  });

  if (!loadButtonEl || !contentWrapperEl) {
    console.error('Cannot initialize pagination, the load button or content wrapper is missing or not found.');
    return;
  }

  reloadPreviousPages({
    components: { loadButtonEl, contentWrapperEl },
    selectors: { parent, loadButton, contentWrapper },
    callbacks: { beforeFetch, afterFetch },
  });

  var nextPage = fetchPageText(loadButtonEl.dataset.nextUrl);
  const loadMore = async () => {
    loadButtonEl.disabled = true;
    loadButtonEl.classList.add('loading');
    await updateContent({
      insertAfter: true,
      data: nextPage,
      components: { counterEl, loadButtonEl, contentWrapperEl },
      selectors: { parent, counter, loadButton, contentWrapper },
      callbacks: { beforeFetch, afterFetch },
    });
    nextPage = fetchPageText(loadButtonEl.dataset.nextUrl);
    loadButtonEl.classList.remove('loading');
    loadButtonEl.disabled = false;
  };

  if (loadButtonEl.dataset.type == 'scroll') {
    const isBtnInPage = () => {
      if (!loadButtonEl.dataset.nextUrl) return false;
      if (!loadButtonEl.closest('html')) {
        window.removeEventListener('scroll', loadScroll);
        return false;
      }
      const { y } = loadButtonEl.getBoundingClientRect();
      return y < window.innerHeight;
    };
    const loadScroll = throttle(async () => {
      if (!isBtnInPage()) return;
      await loadMore();
      loadScroll();
    }, 300);
    loadScroll();
    window.addEventListener('scroll', loadScroll);
  } else {
    loadButtonEl.addEventListener('click', async (e) => {
      e.preventDefault();
      loadMore();
    });
  }
};

export { initPagination };
